import { type ChangeEvent, type KeyboardEvent, memo, useCallback, useEffect, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';

import {
  WORKSPACE_AGENT_CHOICE_CHIP_LINE_HEIGHT_PX,
  WORKSPACE_AGENT_MAX_NUMBERED_OPTIONS,
  workspaceAgentChoiceChipNumber,
  workspaceAgentChoiceFreeTextField,
  workspaceAgentChoiceFreeTextInput,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentChoiceChip';
import { SOMETHING_ELSE_MAX_LENGTH } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/elicitation';

const SOMETHING_ELSE_MAX_LINES = 6;

export type WorkspaceAgentSomethingElseOptionInputProps = {
  index: number;
  label: string;
  onBack: () => void;
  onChange: (value: string) => void;
  value: string;
};

/** Pick one, Something else once chosen: same slot, same number, now a field with a way back to the plain option. */
export const WorkspaceAgentSomethingElseOptionInput = memo(
  ({ index, label, onBack, onChange, value }: WorkspaceAgentSomethingElseOptionInputProps) => {
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // Choosing Something else is asking to type, so the caret goes straight into the field it opened.
    useEffect(() => {
      inputRef.current?.focus({ preventScroll: true });
    }, []);

    // Grow one line at a time with the wrapped text, then scroll inside past the cap.
    useIsomorphicLayoutEffect(() => {
      const field = inputRef.current;

      // biome-ignore lint/suspicious/noUnnecessaryConditions: the ref is populated by React on mount; Biome only sees the initial `null`.
      if (!field) {
        return;
      }

      let lastWidth = -1;
      const fitHeight = () => {
        lastWidth = field.clientWidth;
        field.style.height = 'auto';
        const maxHeight = WORKSPACE_AGENT_CHOICE_CHIP_LINE_HEIGHT_PX * SOMETHING_ELSE_MAX_LINES;
        field.style.height = `${Math.min(field.scrollHeight, maxHeight)}px`;
        field.style.overflowY = field.scrollHeight > maxHeight ? 'auto' : 'hidden';
      };

      fitHeight();

      const observer = new ResizeObserver(() => {
        if (field.clientWidth !== lastWidth) {
          fitHeight();
        }
      });

      observer.observe(field);

      return () => {
        observer.disconnect();
      };
    }, [value]);

    // The answer is one line of text; Enter and pasted newlines stay out so it only wraps.
    const handleChange = useCallback(
      (event: ChangeEvent<HTMLTextAreaElement>) => {
        onChange(event.target.value.replace(/\r?\n/g, ' '));
      },
      [onChange],
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter') {
          event.preventDefault();
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          onBack();
        }
      },
      [onBack],
    );

    return (
      <div className={workspaceAgentChoiceFreeTextField()} data-option-index={index}>
        <span className="flex h-4 shrink-0 items-center">
          {index < WORKSPACE_AGENT_MAX_NUMBERED_OPTIONS ? (
            <span aria-hidden className={workspaceAgentChoiceChipNumber({ selected: true })}>
              {index + 1}
            </span>
          ) : null}
        </span>
        <textarea
          aria-label={label}
          className={workspaceAgentChoiceFreeTextInput({ className: 'block h-4 resize-none overflow-hidden' })}
          data-dock-local-escape=""
          data-testid="WORKSPACE_AGENT_SELECT_SOMETHING_ELSE_INPUT"
          maxLength={SOMETHING_ELSE_MAX_LENGTH}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Describe it…"
          ref={inputRef}
          rows={1}
          value={value}
        />
        <span className="flex h-4 shrink-0 items-center">
          <IconButton appearance="ghost" color="gray" label="Back to options" onClick={onBack} size="extra-small">
            <XIcon className="size-3" />
          </IconButton>
        </span>
      </div>
    );
  },
);

WorkspaceAgentSomethingElseOptionInput.displayName = 'WorkspaceAgentSomethingElseOptionInput';
